import type { MergersAndAcquisitionsRow } from "./deal-tracker-types";

/** Days since a tracker was touched before it reads as ageing, then as stale. */
const FRESHNESS_DAYS = { ageing: 7, stale: 14 } as const;

export type LastUpdateFreshness = "fresh" | "ageing" | "stale" | "never";

/** Sortable and filterable field of a Banker row. */
export type TeamColumnKey =
  "Name" | "ActiveSellSideDeals" | "ActiveBuySideDeals" | "ActiveBakeOffsPitches" | "WeekendHours" | "LastUpdate";

export interface TeamSort {
  key: TeamColumnKey;
  direction: "asc" | "desc";
}

/** Comparison a numeric column filter applies. */
export type NumberFilterOperator = "gte" | "lte" | "eq";

export type TeamFilter =
  | { kind: "text"; value: string }
  | { kind: "number"; operator: NumberFilterOperator; value: number }
  | { kind: "freshness"; value: LastUpdateFreshness };

export type TeamFilters = Partial<Record<TeamColumnKey, TeamFilter>>;

export interface TeamColumn {
  key: TeamColumnKey;
  label: string;
  numeric: boolean;
  /** Which filter control the column's menu offers. */
  filter: TeamFilter["kind"];
}

/** The at-a-glance columns from the Web App Staffer dashboard (TAD-6072 AC 2). */
export const TEAM_COLUMNS: TeamColumn[] = [
  { key: "Name", label: "Banker", numeric: false, filter: "text" },
  { key: "ActiveSellSideDeals", label: "Active Sell-side Deals", numeric: true, filter: "number" },
  { key: "ActiveBuySideDeals", label: "Active Buy-side Deals", numeric: true, filter: "number" },
  { key: "ActiveBakeOffsPitches", label: "Active Bake-offs / Pitches", numeric: true, filter: "number" },
  { key: "WeekendHours", label: "Weekend Hours", numeric: true, filter: "number" },
  { key: "LastUpdate", label: "Last Updated", numeric: false, filter: "freshness" },
];

/** Human labels for the freshness bands, used by the Last Updated filter. */
export const FRESHNESS_LABELS: Record<LastUpdateFreshness, string> = {
  fresh: "Updated this week",
  ageing: "1–2 weeks old",
  stale: "Over 2 weeks old",
  never: "Never updated",
};

/**
 * Freshness banding behind the Last Updated colour coding (TAD-6072 AC 2.5).
 * `today` is passed in rather than read from the clock so the banding is testable
 * and so every row in one render is measured against the same instant.
 */
export function lastUpdateFreshness(lastUpdate: string | null | undefined, today: Date): LastUpdateFreshness {
  if (!lastUpdate || lastUpdate === "N/A") return "never";

  const parsed = Date.parse(lastUpdate);
  if (Number.isNaN(parsed)) return "never";

  const days = Math.floor((today.getTime() - parsed) / (24 * 60 * 60 * 1000));
  if (days >= FRESHNESS_DAYS.stale) return "stale";
  if (days >= FRESHNESS_DAYS.ageing) return "ageing";
  return "fresh";
}

function sortValue(banker: MergersAndAcquisitionsRow, key: TeamColumnKey): number | string {
  if (key === "Name") return (banker.Name ?? "").toLocaleLowerCase();
  if (key === "LastUpdate") {
    const parsed = Date.parse(banker.LastUpdate ?? "");
    // A tracker never updated sorts as the oldest, which is where a Staffer
    // scanning for stale data expects to find it.
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  }
  return banker[key] ?? 0;
}

/** Orders one role's Bankers; the unsorted order is whatever the API returned. */
export function sortBankers(bankers: MergersAndAcquisitionsRow[], sort: TeamSort | null): MergersAndAcquisitionsRow[] {
  if (!sort) return bankers;

  const ordered = [...bankers].sort((left, right) => {
    const a = sortValue(left, sort.key);
    const b = sortValue(right, sort.key);
    if (a === b) return 0;
    return a < b ? -1 : 1;
  });
  return sort.direction === "desc" ? ordered.reverse() : ordered;
}

function matchesNumber(value: number, filter: Extract<TeamFilter, { kind: "number" }>): boolean {
  if (filter.operator === "gte") return value >= filter.value;
  if (filter.operator === "lte") return value <= filter.value;
  return value === filter.value;
}

function matches(banker: MergersAndAcquisitionsRow, key: TeamColumnKey, filter: TeamFilter, today: Date): boolean {
  if (filter.kind === "text") {
    const needle = filter.value.trim().toLocaleLowerCase();
    if (!needle) return true;
    return (banker.Name ?? "").toLocaleLowerCase().includes(needle);
  }

  if (filter.kind === "number") {
    if (Number.isNaN(filter.value)) return true;
    return matchesNumber(Number(banker[key] ?? 0), filter);
  }

  return lastUpdateFreshness(banker.LastUpdate, today) === filter.value;
}

/** Applies every active column filter; a Banker must satisfy all of them. */
export function filterBankers(
  bankers: MergersAndAcquisitionsRow[],
  filters: TeamFilters,
  today: Date
): MergersAndAcquisitionsRow[] {
  const active = Object.entries(filters) as [TeamColumnKey, TeamFilter | undefined][];
  if (active.length === 0) return bankers;

  return bankers.filter((banker) =>
    active.every(([key, filter]) => (filter ? matches(banker, key, filter, today) : true))
  );
}

/** What the grid actually renders for one role: filtered, then ordered. */
export function visibleBankers(
  bankers: MergersAndAcquisitionsRow[],
  filters: TeamFilters,
  sort: TeamSort | null,
  today: Date
): MergersAndAcquisitionsRow[] {
  return sortBankers(filterBankers(bankers, filters, today), sort);
}

export function hasActiveFilters(filters: TeamFilters): boolean {
  return Object.values(filters).some((filter) => filter !== undefined);
}
